(() => {
  if (window.CloudyHeroSectionInitialized) return;
  window.CloudyHeroSectionInitialized = true;

  const initializedSections = new WeakSet();
  const sliderTimers = new WeakMap();
  const dragState = new WeakMap();
  const leaveTimers = new WeakMap();
  const SWIPE_THRESHOLD = 50;
  const ANIMATION_DURATIONS = {
    'fade-in': 450,
    'fade-out': 450,
    'fade-in-out': 450,
    slide: 550
  };

  function clearSliderTimer(section) {
    const timerId = sliderTimers.get(section);
    if (timerId) {
      window.clearInterval(timerId);
      sliderTimers.delete(section);
    }
  }

  function clearLeaveTimer(section) {
    const timerId = leaveTimers.get(section);
    if (timerId) {
      window.clearTimeout(timerId);
      leaveTimers.delete(section);
    }
  }

  function getSlides(section) {
    const slider = section.querySelector('[data-hero-slider]');
    if (!slider) return [];
    return Array.from(slider.querySelectorAll('[data-hero-slide-item]'));
  }

  function getDots(section) {
    return Array.from(section.querySelectorAll('[data-hero-slide-dot]'));
  }

  function getAnimationMode(section) {
    if (!section || !section.dataset) return 'fade-in';
    return section.dataset.sliderAnimation || 'fade-in';
  }

  function getAnimationDuration(section) {
    return ANIMATION_DURATIONS[getAnimationMode(section)] || ANIMATION_DURATIONS['fade-in'];
  }

  function clearSlideAnimationClasses(slide) {
    if (!slide) return;

    slide.classList.remove(
      'is-leaving',
      'hero-slide--enter',
      'hero-slide--leave',
      'hero-slide--direction-next',
      'hero-slide--direction-prev'
    );
  }

  function normalizeSlides(section, activeIndex) {
    const slides = getSlides(section);
    if (!slides.length) return;

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      clearSlideAnimationClasses(slide);
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    section.dataset.activeSlide = String(activeIndex);
  }

  function getCurrentSlideIndex(section, slides) {
    const storedIndex = parseInt(section.dataset.activeSlide || '-1', 10);
    if (!Number.isNaN(storedIndex) && storedIndex >= 0 && storedIndex < slides.length) {
      return storedIndex;
    }

    return slides.findIndex(slide => slide.classList.contains('is-active'));
  }

  function getTransitionDirection(currentIndex, nextIndex, slideCount) {
    if (slideCount <= 1 || currentIndex === nextIndex) return 'next';
    if (currentIndex === slideCount - 1 && nextIndex === 0) return 'next';
    if (currentIndex === 0 && nextIndex === slideCount - 1) return 'prev';
    return nextIndex > currentIndex ? 'next' : 'prev';
  }

  function getDragState(section) {
    if (!dragState.has(section)) {
      dragState.set(section, {
        isPointerDown: false,
        isDragging: false,
        startX: 0,
        startY: 0,
        activePointerId: null,
        suppressClick: false
      });
    }

    return dragState.get(section);
  }

  function resetDragState(section) {
    const state = getDragState(section);
    state.isPointerDown = false;
    state.isDragging = false;
    state.startX = 0;
    state.startY = 0;
    state.activePointerId = null;
    section.classList.remove('is-dragging');
  }

  function setActiveSlide(section, index, options = {}) {
    const slides = getSlides(section);
    if (!slides.length) return;

    const dots = getDots(section);
    const safeIndex = ((index % slides.length) + slides.length) % slides.length;
    let currentIndex = getCurrentSlideIndex(section, slides);

    if (currentIndex < 0) {
      currentIndex = safeIndex;
    }

    clearLeaveTimer(section);

    const shouldAnimate = options.animate !== false && slides.length > 1 && currentIndex !== safeIndex;

    if (shouldAnimate) {
      normalizeSlides(section, currentIndex);

      const previousSlide = slides[currentIndex];
      const nextSlide = slides[safeIndex];
      const direction = getTransitionDirection(currentIndex, safeIndex, slides.length);

      previousSlide.classList.add('is-leaving', 'hero-slide--leave', `hero-slide--direction-${direction}`);
      previousSlide.setAttribute('aria-hidden', 'true');

      nextSlide.classList.add('is-active', 'hero-slide--enter', `hero-slide--direction-${direction}`);
      nextSlide.setAttribute('aria-hidden', 'false');

      section.dataset.activeSlide = String(safeIndex);

      const leaveTimerId = window.setTimeout(() => {
        clearLeaveTimer(section);
        normalizeSlides(section, safeIndex);
      }, getAnimationDuration(section));

      leaveTimers.set(section, leaveTimerId);
    } else {
      normalizeSlides(section, safeIndex);
    }

    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === safeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function moveByDrag(section, deltaX) {
    const slides = getSlides(section);
    if (slides.length <= 1) return false;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return false;

    const currentIndex = parseInt(section.dataset.activeSlide || '0', 10) || 0;
    if (deltaX < 0) {
      setActiveSlide(section, currentIndex + 1);
    } else {
      setActiveSlide(section, currentIndex - 1);
    }

    startSlider(section);
    return true;
  }

  function startSlider(section) {
    if (!section || section.dataset.heroSliderEnabled !== 'true') return;

    const slides = getSlides(section);
    const shouldAutoplay = section.dataset.autoplay === 'true';
    const autoplaySpeed = Math.max(3, parseInt(section.dataset.autoplaySpeed || '5', 10) || 5) * 1000;

    clearSliderTimer(section);

    if (!shouldAutoplay || slides.length <= 1) return;

    const timerId = window.setInterval(() => {
      const currentIndex = parseInt(section.dataset.activeSlide || '0', 10) || 0;
      setActiveSlide(section, currentIndex + 1);
    }, autoplaySpeed);

    sliderTimers.set(section, timerId);
  }

  function initSection(section) {
    if (!section || initializedSections.has(section)) return;
    initializedSections.add(section);

    const slider = section.querySelector('[data-hero-slider]');
    const slides = getSlides(section);
    const dots = getDots(section);

    if (!slider || !slides.length) return;

    setActiveSlide(section, 0, { animate: false });

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        setActiveSlide(section, index);
        startSlider(section);
      });
    });

    if (slides.length > 1) {
      section.addEventListener('mouseenter', () => clearSliderTimer(section));
      section.addEventListener('mouseleave', () => startSlider(section));

      slider.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        const state = getDragState(section);
        state.isPointerDown = true;
        state.isDragging = false;
        state.startX = event.clientX;
        state.startY = event.clientY;
        state.activePointerId = event.pointerId;
        state.suppressClick = false;

        if (slider.setPointerCapture) {
          try {
            slider.setPointerCapture(event.pointerId);
          } catch (error) {
            // Some browsers can reject capture during edge cases; dragging still degrades safely.
          }
        }

        clearSliderTimer(section);
      });

      slider.addEventListener('pointermove', event => {
        const state = getDragState(section);
        if (!state.isPointerDown || state.activePointerId !== event.pointerId) return;

        if (event.pointerType === 'mouse' && (event.buttons & 1) !== 1) {
          resetDragState(section);
          startSlider(section);
          return;
        }

        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;

        if (!state.isDragging) {
          if (Math.abs(deltaX) < 8 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
          state.isDragging = true;
          section.classList.add('is-dragging');
        }

        event.preventDefault();
      });

      function finishDrag(event) {
        const state = getDragState(section);
        if (!state.isPointerDown || state.activePointerId !== event.pointerId) return;

        const deltaX = event.clientX - state.startX;
        const wasDragging = state.isDragging;

        if (slider.releasePointerCapture && slider.hasPointerCapture && slider.hasPointerCapture(event.pointerId)) {
          try {
            slider.releasePointerCapture(event.pointerId);
          } catch (error) {
            // Ignore release failures and complete the drag cleanup.
          }
        }

        resetDragState(section);

        if (wasDragging) {
          const movedSlide = moveByDrag(section, deltaX);
          state.suppressClick = movedSlide;
          if (!movedSlide) {
            startSlider(section);
          }
          return;
        }

        startSlider(section);
      }

      slider.addEventListener('pointerup', finishDrag);
      slider.addEventListener('pointercancel', () => {
        resetDragState(section);
        startSlider(section);
      });
      slider.addEventListener('dragstart', event => {
        event.preventDefault();
      });
      slider.addEventListener('click', event => {
        const state = getDragState(section);
        if (!state.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        state.suppressClick = false;
      }, true);
    }

    startSlider(section);
  }

  function destroySection(section) {
    if (!section) return;
    initializedSections.delete(section);
    clearSliderTimer(section);
    clearLeaveTimer(section);
    dragState.delete(section);
    delete section.dataset.activeSlide;
  }

  function bySectionId(sectionId) {
    return document.getElementById(`shopify-section-${sectionId}`);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.hero-section').forEach(initSection);
  });

  document.addEventListener('shopify:section:load', event => {
    const sectionEl = bySectionId(event.detail.sectionId);
    if (!sectionEl) return;
    initSection(sectionEl.querySelector('.hero-section'));
  });

  document.addEventListener('shopify:section:unload', event => {
    const sectionEl = bySectionId(event.detail.sectionId);
    if (!sectionEl) return;
    destroySection(sectionEl.querySelector('.hero-section'));
  });

  document.addEventListener('shopify:section:select', event => {
    const sectionEl = bySectionId(event.detail.sectionId);
    if (!sectionEl) return;
    clearSliderTimer(sectionEl.querySelector('.hero-section'));
  });

  document.addEventListener('shopify:section:deselect', event => {
    const sectionEl = bySectionId(event.detail.sectionId);
    if (!sectionEl) return;
    startSlider(sectionEl.querySelector('.hero-section'));
  });

  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-hero-slide-item]');
    if (!slide) return;

    const section = slide.closest('.hero-section');
    if (!section) return;

    const slides = getSlides(section);
    const slideIndex = slides.indexOf(slide);
    if (slideIndex === -1) return;

    clearSliderTimer(section);
    setActiveSlide(section, slideIndex);
  });

  document.addEventListener('shopify:block:deselect', event => {
    const slide = event.target.closest('[data-hero-slide-item]');
    if (!slide) return;

    const section = slide.closest('.hero-section');
    if (!section) return;

    startSlider(section);
  });
})();
